import React from 'react';
import Alert from 'src/shared/components/ui/alert';

type ProtectedAreasCoverageNoticeProps = {
    /** Whether the engine returned any overlaps despite the country being uncovered. */
    hasAreasNearby: boolean;
};

const NO_PROTECTED_AREAS_DATA_MESSAGE =
    'We are working to expand our coverage to this area but do not yet have protected areas data for this site’s country.';

/** Shown whenever the site's country sits outside the protected-areas layer. A bare
 *  zero would read as "nothing to disclose", and a bare list would read as complete —
 *  both are the opposite of what we know, which is that this country is not surveyed
 *  yet and anything shown came from across a border.
 *
 *  The test id is fixed rather than passed in: exactly one of these belongs on a page,
 *  so a second is a fault worth failing on rather than a case to name around. The
 *  callout has none of its own — it never appears apart from this notice, so counting
 *  the notice already counts it. */
const ProtectedAreasCoverageNotice = ({ hasAreasNearby }: ProtectedAreasCoverageNoticeProps) => (
    <div className='flex flex-col gap-3' data-testid='protected-areas-coverage-notice'>
        <p
            className='text-sm text-grey-80'
            data-testid='protected-areas-coverage-message'
            data-variant={hasAreasNearby ? 'areas-nearby' : 'no-data'}
        >
            {hasAreasNearby
                ? `Protected Areas shown below fall in neighbouring countries. ${NO_PROTECTED_AREAS_DATA_MESSAGE}`
                : NO_PROTECTED_AREAS_DATA_MESSAGE}
        </p>

        <Alert severity='info'>
            Need this data now? Speak to your account manager. If you can tell us what you need it for and how you would use it, we may be
            able to help.
        </Alert>
    </div>
);

export default ProtectedAreasCoverageNotice;
